import React, { useState, useEffect, useCallback } from "react";
import { Plus, FileText } from "lucide-react";
import { Card, PageHeader, Btn, Input, Select, Field, Modal, Table, Spinner, ErrBox } from "../components/UI";
import { FilterBar, buildQS, initFilters } from "../components/FilterBar";
import Badge from "../components/Badge";
import { fmtRp, fmtDate, statusConfig } from "../utils/format";
import { masterAPI, budgetAPI } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const levelBadge = (val, color) => val ? (
  <span style={{ display:"inline-block", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:600, background:`${color}22`, color, border:`1px solid ${color}44` }}>{val}</span>
) : null;
const ProgramBadge = ({v}) => levelBadge(v, "#7C3AED");
const KegiatanBadge = ({v}) => levelBadge(v, "#D97706");
const KroBadge = ({v}) => levelBadge(v, "#059669");

const RevisiAnggaranPage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const [data, setData]               = useState([]);
  const [loading, setLoading]         = useState(true);
  const [err, setErr]                 = useState("");
  const [filters, setFilters]         = useState(initFilters());
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showAjukan, setShowAjukan]   = useState(false);
  const [anggaranList, setAnggaranList] = useState([]);
  const [form, setForm]               = useState({ anggaran_id:"", pagu_baru:"", alasan:"", dokumen_pendukung:"" });
  const [saving, setSaving]           = useState(false);
  const [aksiModal, setAksiModal]     = useState(null);
  const [catatan, setCatatan]         = useState("");

  const canAjukan  = ["ADMIN","PEJABAT"].includes(role);
  const canVerif   = ["ADMIN","VERIFIKATOR"].includes(role);
  const canApprove = ["ADMIN","PEJABAT"].includes(role);
  const canAck     = ["ADMIN","EKSEKUTIF"].includes(role);

  const STATUS_COLORS = {
    PENDING_VERIFIKASI: { bg:"#FFF7ED", color:"#C2410C", label:"Menunggu Verifikasi" },
    PENDING_APPROVAL:   { bg:"#EFF6FF", color:"#1D4ED8", label:"Menunggu Persetujuan" },
    DISETUJUI:          { bg:"#F0FDF4", color:"#15803D", label:"Disetujui" },
    DITOLAK:            { bg:"#FFF1F2", color:"#BE123C", label:"Ditolak" },
  };

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const qs = buildQS(filters, search, { ...(statusFilter ? { status: statusFilter } : {}) });
      const res = await budgetAPI.getRevisiList(qs);
      setData(res.data || []);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [filters, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadAnggaran = async () => {
    try {
      const res = await budgetAPI.getAll(`?limit=500&tahun=${filters.tahun}`);
      setAnggaranList((res.data||[]).map(a => ({
        value: a.id,
        label: `${a.kode_akun} - ${a.nama_akun} (Pagu: ${fmtRp(a.pagu)})`,
        pagu: a.pagu,
        program_kode: a.program_kode, program_nama: a.program_nama,
        kegiatan_kode: a.kegiatan_kode, kegiatan_nama: a.kegiatan_nama,
        kro_kode: a.kro_kode, kro_nama: a.kro_nama,
      })));
    } catch {}
  };

  const handleAjukan = async () => {
    if (!form.anggaran_id || !form.pagu_baru || !form.alasan) {
      alert("Anggaran, pagu baru, dan alasan wajib diisi"); return;
    }
    setSaving(true);
    try {
      const res = await budgetAPI.ajukanRevisi(form.anggaran_id, {
        pagu_baru: Number(form.pagu_baru),
        alasan: form.alasan,
        dokumen_pendukung: form.dokumen_pendukung || null,
      });
      alert(res.message || "Revisi berhasil diajukan");
      setShowAjukan(false);
      setForm({ anggaran_id:"", pagu_baru:"", alasan:"", dokumen_pendukung:"" });
      load();
    } catch(e) { alert("Gagal: " + e.message); }
    finally { setSaving(false); }
  };

  const handleAksi = async () => {
    if (!aksiModal) return;
    setSaving(true);
    try {
      const res = await budgetAPI.aksiRevisi(aksiModal.revisi.id, { aksi: aksiModal.aksi, catatan });
      alert(res.message || "Berhasil");
      setAksiModal(null); setCatatan("");
      load();
    } catch(e) { alert("Gagal: " + e.message); }
    finally { setSaving(false); }
  };

  const pendingVerif    = data.filter(r => r.status === "PENDING_VERIFIKASI").length;
  const pendingApproval = data.filter(r => r.status === "PENDING_APPROVAL").length;
  const disetujuiBelumAck = data.filter(r => r.status === "DISETUJUI" && !r.dilihat_at).length;

  const aksiForRole = (rev) => {
    const btns = [];
    if (rev.status === "PENDING_VERIFIKASI" && canVerif) {
      btns.push({ aksi:"VERIFIKASI", label:"Verifikasi", color:"#2563EB" });
      btns.push({ aksi:"TOLAK",      label:"Tolak",      color:"#DC2626" });
    }
    if (rev.status === "PENDING_APPROVAL" && canApprove) {
      btns.push({ aksi:"SETUJUI", label:"Setujui", color:"#16A34A" });
      btns.push({ aksi:"TOLAK",   label:"Tolak",   color:"#DC2626" });
    }
    if (rev.status === "DISETUJUI" && canAck && !rev.dilihat_at) {
      btns.push({ aksi:"ACK", label:"Tandai Dibaca", color:"#9333EA" });
    }
    return btns;
  };

  const selRow = form.anggaran_id ? anggaranList.find(a => a.value === form.anggaran_id) : null;

  const selectStyle = { padding:"8px 12px", border:"1.5px solid #E2E8F0", borderRadius:8, fontSize:13, background:"white", color:"#374151", minWidth:160 };

  return (
    <div>
      <PageHeader title="Revisi Anggaran" subtitle="Pengajuan, verifikasi, dan persetujuan perubahan pagu anggaran"
        action={canAjukan && (
          <Btn icon={<Plus size={14}/>} onClick={() => { loadAnggaran(); setShowAjukan(true); }}>
            Ajukan Revisi
          </Btn>
        )}
      />
      <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        {[
          { label:"Menunggu Verifikasi", count:pendingVerif,    color:"#C2410C", bg:"#FFF7ED", show: canVerif },
          { label:"Menunggu Persetujuan", count:pendingApproval, color:"#1D4ED8", bg:"#EFF6FF", show: canApprove },
          { label:"Belum Dikonfirmasi Eksekutif", count:disetujuiBelumAck, color:"#7C3AED", bg:"#F5F3FF", show: canAck },
        ].filter(b => b.show).map(b => (
          <div key={b.label} style={{
            background: b.bg, border:`1.5px solid ${b.color}20`, borderRadius:10,
            padding:"10px 18px", display:"flex", alignItems:"center", gap:10,
          }}>
            <span style={{ fontSize:22, fontWeight:800, color:b.color }}>{b.count}</span>
            <span style={{ fontSize:12, fontWeight:600, color:b.color }}>{b.label}</span>
          </div>
        ))}
      </div>
      <FilterBar filters={filters} onChange={setFilters} search={search} onSearch={setSearch}
        searchPlaceholder="Cari no. revisi, nama akun, alasan…">
        <div>
          <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:5 }}>Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...selectStyle }}>
            <option value="">Semua Status</option>
            <option value="PENDING_VERIFIKASI">Menunggu Verifikasi</option>
            <option value="PENDING_APPROVAL">Menunggu Persetujuan</option>
            <option value="DISETUJUI">Disetujui</option>
            <option value="DITOLAK">Ditolak</option>
          </select>
        </div>
      </FilterBar>
      <Card>
        {err && <div style={{marginBottom:12}}><ErrBox msg={err} onRetry={load}/></div>}
        {loading ? <Spinner/> : data.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 20px", color:"#9CA3AF" }}>
            <FileText size={40} style={{ opacity:0.3, marginBottom:8 }}/>
            <p style={{ margin:0 }}>Belum ada data revisi anggaran</p>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#F8FAFC" }}>
                  {["No. Revisi","Program","Kegiatan","KRO","Akun","Unit","Pagu Lama","Pagu Baru","Selisih","Alasan","Status","Diajukan","Aksi"].map(h => (
                    <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#64748B", textTransform:"uppercase", whiteSpace:"nowrap", borderBottom:"2px solid #E2E8F0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((r, i) => {
                  const sc = STATUS_COLORS[r.status] || { bg:"#F9FAFB", color:"#6B7280", label: r.status };
                  const selisih = Number(r.selisih);
                  const btns = aksiForRole(r);
                  return (
                    <tr key={r.id} style={{ background: i%2===0?"white":"#FAFAFA", borderBottom:"1px solid #F1F5F9" }}>
                      <td style={{ padding:"10px 12px", fontWeight:700, color:"#1E3A5F", whiteSpace:"nowrap" }}>{r.no_revisi || "-"}</td>
                      <td style={{ padding:"10px 12px" }}><ProgramBadge v={r.program_kode}/></td>
                      <td style={{ padding:"10px 12px" }}><KegiatanBadge v={r.kegiatan_kode}/></td>
                      <td style={{ padding:"10px 12px" }}><KroBadge v={r.kro_kode}/></td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ fontWeight:600, fontSize:12 }}>{r.kode_akun}</div>
                        <div style={{ fontSize:11, color:"#6B7280" }}>{r.nama_akun}</div>
                      </td>
                      <td style={{ padding:"10px 12px", fontSize:12 }}>{r.unit}</td>
                      <td style={{ padding:"10px 12px", fontFamily:"monospace", fontSize:12, color:"#6B7280" }}>{fmtRp(r.pagu_lama)}</td>
                      <td style={{ padding:"10px 12px", fontFamily:"monospace", fontSize:12, fontWeight:700 }}>{fmtRp(r.pagu_baru)}</td>
                      <td style={{ padding:"10px 12px", fontFamily:"monospace", fontSize:12, fontWeight:700, color: selisih>=0?"#16A34A":"#DC2626" }}>
                        {selisih>=0?"+":""}{fmtRp(selisih)}
                      </td>
                      <td style={{ padding:"10px 12px", maxWidth:200 }}>
                        <div style={{ fontSize:12, lineHeight:1.4 }}>{r.alasan}</div>
                        {r.catatan_verifikasi && <div style={{ fontSize:11, color:"#2563EB", marginTop:4 }}>Verif: {r.catatan_verifikasi}</div>}
                        {r.catatan_persetujuan && <div style={{ fontSize:11, color:"#16A34A", marginTop:2 }}>Approval: {r.catatan_persetujuan}</div>}
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        <span style={{ background:sc.bg, color:sc.color, padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:"nowrap" }}>
                          {sc.label}
                        </span>
                        {r.status === "DISETUJUI" && (
                          <div style={{ fontSize:10, color: r.dilihat_at ? "#9CA3AF" : "#7C3AED", marginTop:4 }}>
                            {r.dilihat_at ? `Eksekutif` : "Belum dilihat eksekutif"}
                          </div>
                        )}
                      </td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:"#6B7280", whiteSpace:"nowrap" }}>
                        <div>{r.diajukan_oleh_name}</div>
                        <div style={{ color:"#9CA3AF" }}>{r.diajukan_at ? new Date(r.diajukan_at).toLocaleDateString("id-ID") : "-"}</div>
                        {r.diverifikasi_oleh_name && <div style={{ color:"#2563EB", marginTop:2 }}>Verif: {r.diverifikasi_oleh_name}</div>}
                        {r.disetujui_oleh_name  && <div style={{ color:"#16A34A", marginTop:2 }}>Setuju: {r.disetujui_oleh_name}</div>}
                      </td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          {btns.map(b => (
                            <button key={b.aksi} onClick={() => { setAksiModal({ revisi:r, aksi:b.aksi }); setCatatan(""); }}
                              style={{ padding:"5px 12px", background:b.color, color:"white", border:"none",
                                borderRadius:6, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap" }}>
                              {b.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showAjukan && (
        <Modal title="Ajukan Revisi Anggaran" onClose={() => setShowAjukan(false)}>
          <Field label="Pilih Anggaran" required>
            <Select value={form.anggaran_id}
              onChange={e => setForm({ ...form, anggaran_id: e.target.value, pagu_baru:"" })}
              options={anggaranList} placeholder="Pilih anggaran yang akan direvisi…"/>
          </Field>
          {selRow && (
            <div style={{ background:"#F0F9FF", border:"1px solid #BAE6FD", borderRadius:8, padding:"10px 14px", marginBottom:12, fontSize:12 }}>
              <div style={{ marginBottom:4 }}>
                <span style={{ color:"#0369A1", fontWeight:600 }}>Pagu saat ini: </span>
                <span style={{ fontFamily:"monospace", fontWeight:700 }}>{fmtRp(selRow.pagu)}</span>
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                <ProgramBadge v={selRow.program_kode ? `${selRow.program_kode} - ${selRow.program_nama}` : null}/>
                <KegiatanBadge v={selRow.kegiatan_kode ? `${selRow.kegiatan_kode} - ${selRow.kegiatan_nama}` : null}/>
                <KroBadge v={selRow.kro_kode ? `${selRow.kro_kode} - ${selRow.kro_nama}` : null}/>
              </div>
            </div>
          )}
          <Field label="Pagu Baru (Rp)" required>
            <Input type="number" value={form.pagu_baru}
              onChange={e => setForm({ ...form, pagu_baru: e.target.value })}
              placeholder="Masukkan pagu baru…"/>
          </Field>
          {form.pagu_baru && selRow && (
            <div style={{ background: Number(form.pagu_baru) >= selRow.pagu ? "#F0FDF4" : "#FFF1F2",
              border:`1px solid ${Number(form.pagu_baru) >= selRow.pagu ? "#BBF7D0":"#FECDD3"}`,
              borderRadius:8, padding:"8px 14px", marginBottom:12, fontSize:12 }}>
              <span style={{ fontWeight:600 }}>Selisih: </span>
              <span style={{ fontFamily:"monospace", fontWeight:700, color: Number(form.pagu_baru) >= selRow.pagu ? "#16A34A" : "#DC2626" }}>
                {Number(form.pagu_baru) >= selRow.pagu ? "+" : ""}{fmtRp(Number(form.pagu_baru) - selRow.pagu)}
              </span>
            </div>
          )}
          <Field label="Alasan Revisi" required>
            <textarea value={form.alasan} onChange={e => setForm({ ...form, alasan: e.target.value })}
              placeholder="Jelaskan alasan pengajuan revisi pagu anggaran…"
              rows={3} style={{ width:"100%", padding:"8px 12px", border:"1.5px solid #E2E8F0",
                borderRadius:8, fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }}/>
          </Field>
          <Field label="Dokumen Pendukung (URL/No. Surat)">
            <Input value={form.dokumen_pendukung}
              onChange={e => setForm({ ...form, dokumen_pendukung: e.target.value })}
              placeholder="Nomor surat atau link dokumen (opsional)"/>
          </Field>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
            <Btn variant="secondary" onClick={() => setShowAjukan(false)}>Batal</Btn>
            <Btn onClick={handleAjukan} disabled={saving}>{saving ? "Mengajukan…" : "Ajukan Revisi"}</Btn>
          </div>
        </Modal>
      )}

      {aksiModal && (
        <Modal
          title={
            aksiModal.aksi === "VERIFIKASI" ? "Verifikasi Revisi Anggaran" :
            aksiModal.aksi === "SETUJUI"    ? "Setujui Revisi Anggaran" :
            aksiModal.aksi === "TOLAK"      ? "Tolak Revisi Anggaran" :
            "Konfirmasi Telah Dibaca"
          }
          onClose={() => setAksiModal(null)}
        >
          <div style={{ background:"#F8FAFC", borderRadius:8, padding:"12px 14px", marginBottom:16, fontSize:13 }}>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              <div><span style={{ color:"#6B7280" }}>No. Revisi:</span><br/><strong>{aksiModal.revisi.no_revisi}</strong></div>
              <div><span style={{ color:"#6B7280" }}>Akun:</span><br/><strong>{aksiModal.revisi.kode_akun} - {aksiModal.revisi.nama_akun}</strong></div>
              <div><span style={{ color:"#6B7280" }}>Pagu Lama:</span><br/><span style={{ fontFamily:"monospace" }}>{fmtRp(aksiModal.revisi.pagu_lama)}</span></div>
              <div><span style={{ color:"#6B7280" }}>Pagu Baru:</span><br/><span style={{ fontFamily:"monospace", fontWeight:700 }}>{fmtRp(aksiModal.revisi.pagu_baru)}</span></div>
            </div>
            <div style={{ marginTop:8 }}><span style={{ color:"#6B7280" }}>Alasan:</span><br/>{aksiModal.revisi.alasan}</div>
          </div>
          {aksiModal.aksi !== "ACK" && (
            <Field label={aksiModal.aksi === "TOLAK" ? "Alasan Penolakan" : "Catatan (opsional)"}>
              <textarea value={catatan} onChange={e => setCatatan(e.target.value)}
                placeholder={aksiModal.aksi === "TOLAK" ? "Wajib diisi untuk penolakan…" : "Catatan tambahan…"}
                rows={3} style={{ width:"100%", padding:"8px 12px", border:"1.5px solid #E2E8F0",
                  borderRadius:8, fontSize:13, resize:"vertical", boxSizing:"border-box", fontFamily:"inherit" }}/>
            </Field>
          )}
          {aksiModal.aksi === "ACK" && (
            <p style={{ fontSize:13, color:"#6B7280", marginBottom:16 }}>
              Konfirmasi bahwa Anda telah membaca dan mengetahui revisi anggaran yang telah disetujui ini.
            </p>
          )}
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <Btn variant="secondary" onClick={() => setAksiModal(null)}>Batal</Btn>
            <Btn
              onClick={handleAksi}
              disabled={saving || (aksiModal.aksi === "TOLAK" && !catatan)}
              style={{
                background:
                  aksiModal.aksi === "TOLAK"      ? "#DC2626" :
                  aksiModal.aksi === "SETUJUI"     ? "#16A34A" :
                  aksiModal.aksi === "VERIFIKASI"  ? "#2563EB" : "#7C3AED",
              }}
            >
              {saving ? "Memproses…" :
                aksiModal.aksi === "VERIFIKASI"  ? "Verifikasi" :
                aksiModal.aksi === "SETUJUI"     ? "Setujui & Terapkan Pagu" :
                aksiModal.aksi === "TOLAK"       ? "Tolak Revisi" :
                "Konfirmasi Dibaca"}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default RevisiAnggaranPage;
